"use client";

import {
  useCallback,
  useEffect,
  useRef,
  useState,
  type CSSProperties,
  type ReactNode,
} from "react";
import type { DiscoveredProject } from "../core/discovery";
import type { ConnectionStatus } from "../core/types";
import { ProjectPicker } from "./connection/ProjectPicker";
import { formSlot, type FormSlot } from "./connection/styles";

/** What people type as Flow's address; the client adds `/sources` itself. */
export const DEFAULT_FLOW_ADDRESS = "https://flow.sparknest.it/";

/**
 * What the form shows and edits. `GET /api/support/status` returns exactly this (plus
 * `enabled` and `connectionId`); `useSupportConnection()` wires the form to those routes.
 */
export type ConnectionFormValue = {
  dashboardUrl: string;
  /** The proposal: `space/project` on Flow, a free key on the retired dashboard. */
  project: string;
  status: ConnectionStatus | "disabled";
  lastError?: string | null;
};

export type ConnectionFormProps = {
  value: ConnectionFormValue;
  /** «Richiedi connessione»: the host saves URL/project and starts the pairing. */
  onRequest: (draft: { dashboardUrl: string; project: string }) => Promise<void> | void;
  /** «Disattiva»: the host forgets the pairing and stops reporting. */
  onDisconnect: () => Promise<void> | void;
  /** Called on a timer while `pending` (fast) and `active` (slow) so the status refreshes. */
  onPoll?: () => Promise<void> | void;
  /**
   * Projects offered at an address (`GET /api/support/projects`). With it the form shows
   * «Spazio» + «Progetto»; `null` (or no callback) means a single free «Progetto» field.
   */
  onDiscover?: (dashboardUrl: string) => Promise<DiscoveredProject[] | null>;
  disabled?: boolean;
  title?: string;
  /** Replaces the default explanatory sentence under the fields. */
  hint?: ReactNode;
  placeholders?: { dashboardUrl?: string; project?: string; space?: string };
  /** Poll cadence: while pending / while active. Defaults 3 s / 20 s. */
  pollMs?: { pending?: number; active?: number };
  /** Optional class hooks for hosts with a CSS system (Helios-style `card`, `input`, …). */
  classNames?: Partial<Record<FormSlot, string>>;
  /** Inline-style overrides for hosts without a CSS system (memo-style). */
  styles?: Partial<Record<FormSlot, CSSProperties>>;
};

export const STATUS_LABELS: Record<ConnectionFormValue["status"], string> = {
  not_configured: "Da configurare",
  pending: "In attesa di approvazione",
  active: "Connesso",
  rejected: "Richiesta rifiutata",
  error: "Errore di collegamento",
  disabled: "Disattivato",
};

const STATUS_COLORS: Record<ConnectionFormValue["status"], string> = {
  not_configured: "#94a3b8",
  pending: "#f59e0b",
  active: "#10b981",
  rejected: "#ef4444",
  error: "#ef4444",
  disabled: "#94a3b8",
};

/** `https://flow.sparknest.it/sources` reads better as the address people know. */
const displayAddress = (url: string) => url.replace(/\/sources\/?$/, "/");

/**
 * «Connessione a Flow» — the settings card every host shows: Flow's address, the project
 * (chosen among Flow's spaces and projects when discovery is available), one sentence of
 * explanation, and a single action that depends on the state («Richiedi connessione» until
 * approved, «Disattiva» once connected). Polls while pending so the approval shows up.
 *
 * Presentation is neutral: pass `classNames` in a host with a CSS system, or `styles` to
 * blend with inline-styled hosts. Behaviour stays on the host side through the callbacks.
 */
export function MemoProjectConnectionForm({
  value,
  onRequest,
  onDisconnect,
  onPoll,
  onDiscover,
  disabled = false,
  title = "Connessione a Flow",
  hint,
  placeholders,
  pollMs,
  classNames = {},
  styles = {},
}: ConnectionFormProps) {
  const theme = { classNames, styles };
  const [url, setUrl] = useState(displayAddress(value.dashboardUrl) || DEFAULT_FLOW_ADDRESS);
  const [project, setProject] = useState(value.project);
  const [projects, setProjects] = useState<DiscoveredProject[] | null>(null);
  const [discovering, setDiscovering] = useState(false);
  const [busy, setBusy] = useState(false);
  const discovered = useRef<string | null>(null);

  useEffect(() => {
    setUrl(displayAddress(value.dashboardUrl) || DEFAULT_FLOW_ADDRESS);
    setProject(value.project);
  }, [value.dashboardUrl, value.project]);

  const discover = useCallback(
    async (address: string) => {
      const target = address.trim();
      if (!onDiscover || !target || discovered.current === target) return;
      discovered.current = target;
      setDiscovering(true);
      try {
        setProjects(await onDiscover(target));
      } catch {
        setProjects(null);
      } finally {
        setDiscovering(false);
      }
    },
    [onDiscover],
  );

  // On mount (and when the saved address changes) with an address already there.
  useEffect(() => {
    const saved = displayAddress(value.dashboardUrl) || DEFAULT_FLOW_ADDRESS;
    void discover(saved);
  }, [value.dashboardUrl, discover]);

  // Pending: poll fast to catch the approval. Active: slow check to notice a revocation.
  useEffect(() => {
    if (!onPoll) return;
    const every =
      value.status === "pending"
        ? (pollMs?.pending ?? 3_000)
        : value.status === "active"
          ? (pollMs?.active ?? 20_000)
          : 0;
    if (!every) return;
    const t = setInterval(() => void onPoll(), every);
    return () => clearInterval(t);
  }, [value.status, onPoll, pollMs?.pending, pollMs?.active]);

  const s = (k: FormSlot, extra?: CSSProperties) => formSlot(theme, k, extra);
  const locked = disabled || busy;
  const connected = value.status === "active";
  const canRequest = url.trim() !== "" && project.trim() !== "" && !discovering;
  const onPicked = useCallback((scope: string) => setProject(scope), []);

  const run = async (fn: () => Promise<void> | void) => {
    setBusy(true);
    try {
      await fn();
    } finally {
      setBusy(false);
    }
  };

  return (
    <div {...s("card")}>
      <h3 {...s("title")}>
        <span>{title}</span>
        <span {...s("status")}>
          <span
            style={{
              width: 8,
              height: 8,
              borderRadius: "50%",
              background: STATUS_COLORS[value.status],
              boxShadow: `0 0 6px ${STATUS_COLORS[value.status]}`,
            }}
          />
          {STATUS_LABELS[value.status]}
        </span>
      </h3>

      <label {...s("field")}>
        <span {...s("label")}>Indirizzo di Flow</span>
        <input
          {...s("input")}
          disabled={locked}
          placeholder={placeholders?.dashboardUrl ?? DEFAULT_FLOW_ADDRESS}
          value={url}
          spellCheck={false}
          onChange={(e) => setUrl(e.target.value)}
          onBlur={() => void discover(url)}
          onKeyDown={(e) => {
            if (e.key === "Enter") {
              e.preventDefault();
              void discover(url);
            }
          }}
        />
      </label>

      {discovering ? (
        <div {...s("hint", { display: "block", marginTop: 12 })}>Ricerca dei progetti…</div>
      ) : projects ? (
        <ProjectPicker
          projects={projects}
          initial={project}
          onChange={onPicked}
          disabled={locked}
          theme={theme}
          placeholders={{ space: placeholders?.space, project: placeholders?.project }}
        />
      ) : (
        <label {...s("field")}>
          <span {...s("label")}>Progetto</span>
          <input
            {...s("input")}
            disabled={locked}
            placeholder={placeholders?.project ?? "nome-progetto"}
            value={project}
            spellCheck={false}
            onChange={(e) => setProject(e.target.value)}
          />
        </label>
      )}

      {value.lastError && (
        <div style={{ marginTop: 12, fontSize: 13, color: "#ef4444" }}>{value.lastError}</div>
      )}

      <div style={{ display: "flex", alignItems: "center", gap: 16, marginTop: 18 }}>
        <span {...s("hint")}>
          {hint ??
            "Flow approva o rifiuta la richiesta. Dopo l'approvazione rilascia un token custodito sul server e limitato al progetto scelto."}
        </span>
        {connected ? (
          <button
            type="button"
            {...s("button")}
            disabled={locked}
            onClick={() => void run(onDisconnect)}
          >
            Disattiva
          </button>
        ) : (
          <button
            type="button"
            {...s("buttonPrimary")}
            disabled={locked || !canRequest}
            onClick={() =>
              void run(() => onRequest({ dashboardUrl: url.trim(), project: project.trim() }))
            }
          >
            {busy
              ? "Invio…"
              : value.status === "pending"
                ? "Invia di nuovo"
                : "Richiedi connessione"}
          </button>
        )}
      </div>
    </div>
  );
}
